import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuthModal } from "../../contexts/AuthModalContext";

interface HeroSlide {
  key: string;
  image: string;
  href: string;
  position?: string;
}

const slides: HeroSlide[] = [
  {
    key: "slide2",
    image: "/images/banners/banner-2.png",
    href: "/sell",
  },
  {
    key: "slide3",
    image: "/images/banners/banner-3.png",
    href: "/catalog",
  },
  {
    key: "slide4",
    image: "/images/banners/banner-4.png",
    href: "/catalog",
  },
];

export default function Hero() {
  const { t } = useTranslation();
  const { openAuthModal } = useAuthModal();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState<Set<number>>(() => new Set([0]));

  const next = useCallback(
    () =>
      setCurrent((c) => {
        const nextIdx = (c + 1) % slides.length;
        setLoaded((prev) => {
          if (prev.has(nextIdx)) return prev;
          const copy = new Set(prev);
          copy.add(nextIdx);
          return copy;
        });
        return nextIdx;
      }),
    [],
  );
  const prev = () =>
    setCurrent((c) => {
      const prevIdx = (c - 1 + slides.length) % slides.length;
      setLoaded((p) => {
        if (p.has(prevIdx)) return p;
        const copy = new Set(p);
        copy.add(prevIdx);
        return copy;
      });
      return prevIdx;
    });

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [paused, next]);

  // Preload the next slide image
  useEffect(() => {
    const nextIdx = (current + 1) % slides.length;
    if (!loaded.has(nextIdx)) {
      const img = new Image();
      img.src = slides[nextIdx].image;
      img.onload = () =>
        setLoaded((prev) => {
          const copy = new Set(prev);
          copy.add(nextIdx);
          return copy;
        });
    }
  }, [current, loaded]);

  const slide = slides[current];

  return (
    <section
      className="hero-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Achtergrondafbeeldingen (fade) — alleen laden als nodig */}
      {slides.map((s, i) => (
        <div
          key={s.key}
          className={`hero-slide-bg${i === current ? " active" : ""}`}
          style={
            loaded.has(i)
              ? {
                  backgroundImage: `url(${s.image})`,
                  ...(s.position ? { backgroundPosition: s.position } : {}),
                }
              : undefined
          }
          aria-hidden="true"
        />
      ))}

      {/* Donkere overlay */}
      <div className="hero-overlay" />

      {/* Inhoud */}
      <div className="hero-content">
        <h1 className="hero-heading">{t(`hero.${slide.key}Title`)}</h1>
        <p className="hero-sub">{t(`hero.${slide.key}Text`)}</p>
        {slide.key === "slide4" ? (
          <button type="button" className="hero-cta" onClick={openAuthModal}>
            {t(`hero.${slide.key}Cta`)}
          </button>
        ) : (
          <a href={slide.href} className="hero-cta">
            {t(`hero.${slide.key}Cta`)}
          </a>
        )}
      </div>

      {/* Pijlen */}
      <button
        className="hero-arrow hero-arrow--prev"
        onClick={prev}
        aria-label={t("hero.prevSlide")}
      >
        &#8592;
      </button>
      <button
        className="hero-arrow hero-arrow--next"
        onClick={next}
        aria-label={t("hero.nextSlide")}
      >
        &#8594;
      </button>
    </section>
  );
}
